/**
 * Per-route metadata.
 *
 * Single source of truth, read three ways: by the <Seo> component for
 * client-side navigation, by the prerender script when it writes each route's
 * static HTML, and by the sitemap generator.
 *
 * Descriptions are written from what each page actually contains. Nothing here
 * asserts a rating, a testimonial or a business claim the site does not make.
 */

export interface RouteMeta {
  path: string;
  title: string;
  description: string;
  /** Kept out of the sitemap and marked noindex. */
  noindex?: boolean;
}

export const SITE_NAME = 'World Finance Terminal';

export const SITE_URL = (import.meta.env?.VITE_SITE_URL ?? 'https://wft.godengine.ai').replace(
  /\/$/,
  ''
);

export const ROUTE_META: RouteMeta[] = [
  {
    path: '/',
    title: 'World Finance Terminal by GodEngine',
    description:
      "A terminal for the world's open financial data. Twenty sections, seventeen connectors and two engines — every series carries a GodEngine forecast scored against reality in public.",
  },
  {
    path: '/platform',
    title: 'Platform — World Finance Terminal',
    description:
      'Twenty sections across tape, context, engine and desk. Panel anatomy, connector coverage per cluster, responsive layouts and the design principles behind the terminal frame.',
  },
  {
    path: '/engine',
    title: 'Engine — World Finance Terminal',
    description:
      'TimesFM 2.5 produces quantile forecasts; the GodEngine swarm resolver produces branching scenarios. The forecast path, the integrity checks on vendored weights, and the provenance recorded with every run.',
  },
  {
    path: '/data',
    title: 'Data sources — World Finance Terminal',
    description:
      'Seventeen connectors with their cadence, key requirement and redistribution terms. Free and keyless, free with a key, paid and optional — plus the six source states a panel can be in.',
  },
  {
    path: '/record',
    title: 'Track record — World Finance Terminal',
    description:
      'How a forecast is requested, written, resolved and scored. Coverage at 80, skill against a random walk, calibration curves — and the rules that stop a record from flattering itself.',
  },
  {
    path: '/design',
    title: 'Design system — World Finance Terminal',
    description:
      'The token layer behind both themes: colour roles, the six-step type scale, the rules governing when a number may take colour, and the three display densities.',
  },
  {
    path: '/access',
    title: 'Access — World Finance Terminal',
    description:
      'Self-hosting the stack: six setup steps, the deliberately non-default ports, which environment keys unlock which sections, and the operational rules around the model weights.',
  },
  {
    path: '/faq',
    title: 'FAQ — World Finance Terminal',
    description:
      'Twelve questions on data sources, empty panels, the engine, the track record, redistribution terms, self-hosting and the two themes.',
  },
  {
    path: '/login',
    title: 'Sign in — World Finance Terminal',
    description:
      'Design for the sign-in screen. Authentication has no endpoint yet; this gates the browser-only demo.',
    noindex: true,
  },
  {
    path: '/terminal',
    title: 'Terminal — World Finance Terminal',
    description: 'Browser-only recreation of the terminal surface with no API reachable.',
    noindex: true,
  },
];

export const DEFAULT_META = ROUTE_META[0];

export function metaForPath(pathname: string): RouteMeta {
  const clean = pathname.replace(/\/+$/, '') || '/';
  return ROUTE_META.find((m) => m.path === clean) ?? DEFAULT_META;
}

/** Routes that belong in the sitemap. */
export function indexableRoutes(): RouteMeta[] {
  return ROUTE_META.filter((m) => !m.noindex);
}
